// Version the dependency as Shopify's asset_url version on this module does not propagate to imports.
import { BalloonPool, BalloonReaction, BalloonMotion, balloonSettings, balloonFlight, balloonFlightFrames, between, clamp } from './lb-balloon-engine.js?v=2026101605';

const instances = new Map();

export class LaunchScene {
  constructor(root) {
    this.root = root;
    this.sky = root.querySelector('[data-lb-sky]');
    this.motion = root.querySelector('[data-lb-motion]');
    this.wordmark = root.querySelector('[data-lb-wordmark]');
    this.logo = root.querySelector('[data-lb-logo]');
    this.hero = root.querySelector('.lb-launch__hero');
    this.dock = root.querySelector('[data-lb-dock]');
    this.assets = new Map([...root.querySelector('template').content.querySelectorAll('[data-shape]')].map((image) => [image.dataset.shape, image.dataset.src]));
    this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
    this.mobile = matchMedia('(max-width: 749px)');
    this.heroActive = this.hero.getBoundingClientRect().bottom > innerHeight / 2;
    this.pool = new BalloonPool([...this.assets.keys()], this.settings.limit);
    this.balloons = new Map();
    this.abort = new AbortController();
    this.userPaused = false;
    this.destroyed = false;
    this.pendingPointer = null;
    this.pointer = null;
    this.grab = null;
    this.frame = 0;
    this.timer = 0;
    this.resizeTimer = 0;
    this.cookieFrame = 0;
    this.lastFrame = 0;
    this.lastWidth = innerWidth;
    this.lastHeight = innerHeight;
    const on = (target, type, handler, options = {}) => target.addEventListener(type, handler, { ...options, signal: this.abort.signal });
    this.motion.hidden = false;
    this.wordmark.hidden = false;
    this.observer = new IntersectionObserver(([entry]) => {
      const visible = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
      this.wordmark.toggleAttribute('data-visible', visible);
      this.wordmark.inert = !visible;
    });
    this.observer.observe(this.logo);
    this.foldObserver = new IntersectionObserver(() => this.updateFold(), { threshold: [0, .5, 1] });
    this.foldObserver.observe(this.hero);
    on(this.motion, 'click', () => {
      this.userPaused = !this.userPaused;
      this.motion.setAttribute('aria-pressed', String(this.userPaused));
      this.motion.setAttribute('aria-label', this.userPaused ? this.motion.dataset.resumeLabel : this.motion.dataset.pauseLabel);
      this.sync();
    });
    on(document, 'visibilitychange', () => this.sync());
    on(this.reduced, 'change', () => this.restart());
    on(window, 'resize', () => {
      this.endGrab(null, true);
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        if (innerWidth !== this.lastWidth || Math.abs(innerHeight - this.lastHeight) > 120) this.restart();
        this.updateConsent();
      }, 180);
    }, { passive: true });
    on(document, 'pointermove', (event) => this.pointerMove(event), { passive: true });
    on(this.sky, 'pointerdown', (event) => this.startGrab(event));
    on(document, 'pointerup', (event) => this.endGrab(event));
    on(document, 'pointercancel', (event) => this.endGrab(event, true));
    on(this.sky, 'lostpointercapture', (event) => this.endGrab(event, true));
    on(document, 'pointerout', (event) => { if (!event.relatedTarget) this.pointer = null; }, { passive: true });
    on(this.sky, 'click', (event) => {
      if (this.paused) return;
      const balloon = event.target.closest('[data-balloon-id]');
      const record = balloon && this.balloons.get(Number(balloon.dataset.balloonId));
      if (record && performance.now() >= record.suppressClickUntil) {
        const box = record.position.getBoundingClientRect();
        const nx = clamp((event.clientX - box.left - box.width / 2) / (box.width / 2), -1, 1);
        const ny = clamp((event.clientY - box.top - box.height / 2) / (box.height / 2), -1, 1);
        record.movement.kick(nx, ny);
        record.reaction.click(nx, ny);
        this.requestFrame();
      }
    });
    const form = root.querySelector('.lb-launch__form');
    const submit = form.querySelector('[data-lb-submit]');
    if (submit) {
      const originalLabel = submit.textContent;
      on(form, 'submit', () => {
        submit.disabled = true;
        submit.textContent = submit.dataset.pendingLabel;
        form.setAttribute('aria-busy', 'true');
      });
      on(window, 'pageshow', () => {
        submit.disabled = false;
        submit.textContent = originalLabel;
        form.removeAttribute('aria-busy');
      });
    }
    const feedback = root.querySelector('[data-lb-feedback]');
    if (feedback) {
      feedback.scrollIntoView({ block: 'center', behavior: 'instant' });
      feedback.focus({ preventScroll: true });
    }
    this.cookieObserver = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => !this.root.contains(mutation.target))) this.queueConsent();
    });
    this.cookieObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'hidden', 'aria-hidden', 'open'] });
    this.cookieResize = new ResizeObserver(() => this.queueConsent());
    this.cookieElements = new Set();
    this.cookieRoots = new WeakSet();
    this.updateConsent();
    this.restart();
  }

  get paused() { return this.destroyed || this.userPaused || this.reduced.matches || document.hidden; }
  get settings() { return balloonSettings(this.mobile.matches, this.heroActive); }
  get viewportWidth() { return document.documentElement.clientWidth || innerWidth; }

  updateFold() {
    const box = this.hero.getBoundingClientRect();
    const hero = box.top <= innerHeight / 2 && box.bottom > innerHeight / 2;
    this.root.dataset.fold = hero ? 'hero' : 'message';
    this.root.style.setProperty('--lb-static-size', `${this.baseSize() * balloonSettings(this.mobile.matches, hero).scale}px`);
    if (hero === this.heroActive) return;
    this.heroActive = hero;
    this.pool.limit = this.settings.limit;
    for (const record of this.balloons.values()) this.resizeBalloon(record);
    if (!this.paused && hero) {
      const current = this.balloons.size;
      for (let i = current; i < this.settings.initial; i++) this.spawn(i, (i - current) * 160);
    }
  }

  baseSize() {
    const safeWidth = Math.max(this.root.querySelector('.lb-launch__message').getBoundingClientRect().width, this.root.querySelector('.lb-launch__footer').getBoundingClientRect().width);
    return this.mobile.matches ? 36 : Math.min(132, Math.max(42, (this.viewportWidth - safeWidth) / 2 - 60));
  }

  resizeBalloon(record) {
    record.size = this.baseSize() * this.settings.scale * record.depth;
    record.element.style.width = `${record.size}px`;
    record.element.style.height = `${record.size}px`;
  }

  restart() {
    this.clearBalloons();
    this.lastWidth = innerWidth;
    this.lastHeight = innerHeight;
    this.heroActive = this.hero.getBoundingClientRect().bottom > innerHeight / 2;
    this.root.dataset.fold = this.heroActive ? 'hero' : 'message';
    this.pool.limit = this.settings.limit;
    this.root.removeAttribute('data-animated');
    this.root.style.setProperty('--lb-static-size', `${this.baseSize() * this.settings.scale}px`);
    if (!this.paused) {
      const initial = this.settings.initial;
      this.root.setAttribute('data-animated', '');
      for (let i = 0; i < initial; i++) this.spawn(i, i * 160);
    }
    this.sync();
  }

  sync() {
    clearTimeout(this.timer);
    this.timer = 0;
    this.root.toggleAttribute('data-paused', this.paused);
    if (this.paused) this.endGrab(null, true);
    for (const record of this.balloons.values()) {
      if (record.animation) (this.paused || this.grab?.record === record) ? record.animation.pause() : record.animation.play();
    }
    if (this.paused) {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.lastFrame = 0;
      this.pendingPointer = null;
      this.pointer = null;
    } else {
      if (!this.balloons.size) {
        this.root.setAttribute('data-animated', '');
        for (let i = 0; i < this.settings.initial; i++) this.spawn(i, i * 160);
      }
      this.schedule();
      this.requestFrame();
    }
  }

  schedule() {
    if (this.paused) return;
    this.timer = setTimeout(() => {
      this.timer = 0;
      const [min, max] = this.settings.burst;
      const count = Math.floor(between(min, max + 1));
      for (let i = 0; i < count; i++) this.spawn();
      this.schedule();
    }, between(2000, 5000));
  }

  spawn(initialIndex, delay = 0) {
    if (this.paused) return;
    const ticket = this.pool.reserve();
    if (!ticket) return;
    const depth = between(.85, 1.15);
    const size = this.baseSize() * this.settings.scale * depth;
    const choice = Math.random();
    const edge = choice < .5 ? 'bottom' : choice < .75 ? 'left' : 'right';
    const side = edge === 'left' ? 0 : edge === 'right' ? 1 : initialIndex === undefined ? (Math.random() < .5 ? 0 : 1) : initialIndex % 2;
    let location, flight;
    const protectedBoxes = this.protectedBoxes();
    for (let attempt = 0; attempt < 6 && !flight; attempt++) {
      location = this.placeBalloon(size, side);
      if (!location) break;
      flight = balloonFlight({ width: this.viewportWidth, height: innerHeight, size, target: location, protectedBoxes, edge });
    }
    if (!flight) { this.pool.release(ticket.id); return; }
    const { x, y } = location;
    const element = document.createElement('div');
    element.className = 'lb-launch__balloon';
    element.dataset.balloonId = ticket.id;
    element.dataset.shape = ticket.shape;
    element.dataset.entry = edge;
    Object.assign(element.style, { left: `${x}px`, top: `${y}px`, width: `${size}px`, height: `${size}px`, opacity: '0' });
    element.style.setProperty('--lb-sway', `${between(5, 8)}s`);
    element.style.setProperty('--lb-delay', `${between(-5, 0)}s`);
    const position = document.createElement('div');
    position.className = 'lb-launch__balloon-position';
    const body = document.createElement('div');
    body.className = 'lb-launch__balloon-body';
    const image = new Image();
    image.alt = '';
    image.width = 160;
    image.height = 160;
    image.draggable = false;
    image.decoding = 'async';
    image.fetchPriority = 'low';
    body.append(image);
    position.append(body);
    element.append(position);
    this.sky.append(element);
    const record = { ...ticket, element, position, body, image, side, x, y, size, depth, flight, movement: new BalloonMotion(), reaction: new BalloonReaction(), animation: null, suppressClickUntil: 0 };
    this.balloons.set(ticket.id, record);
    image.onload = () => {
      if (this.destroyed || !this.balloons.has(ticket.id)) return;
      this.root.setAttribute('data-animated', '');
      record.keyframes = balloonFlightFrames(flight, location, -(innerHeight + size * 3 + 100));
      record.animation = element.animate(record.keyframes, { duration: flight.duration, delay, fill: 'both' });
      if (this.paused) record.animation.pause();
      record.animation.finished.then(() => this.remove(ticket.id), () => {});
    };
    image.onerror = () => this.remove(ticket.id);
    image.src = this.assets.get(ticket.shape);
  }

  remove(id) {
    const record = this.balloons.get(id);
    if (!record) return;
    if (this.grab?.record === record) this.endGrab(null, true);
    record.image.onload = null;
    record.image.onerror = null;
    record.animation?.cancel();
    record.element.remove();
    this.balloons.delete(id);
    this.pool.release(id);
  }

  pointerMove(event) {
    if (this.grab?.pointerId === event.pointerId) {
      const { record } = this.grab;
      if (record.movement.move(event.clientX, event.clientY, performance.now())) record.element.setAttribute('data-dragging', '');
      this.requestFrame();
      return;
    }
    if (this.paused || event.pointerType === 'touch') return;
    const now = performance.now();
    if (this.pointer) {
      const elapsed = Math.max(8, now - this.pointer.time);
      const speed = Math.hypot(event.clientX - this.pointer.x, event.clientY - this.pointer.y) / elapsed;
      this.pendingPointer = { x: event.clientX, y: event.clientY, speed: clamp(speed, 0, 3) };
      this.requestFrame();
    }
    this.pointer = { x: event.clientX, y: event.clientY, time: now };
  }

  requestFrame() {
    if (this.frame || this.paused) return;
    this.frame = requestAnimationFrame((now) => this.tick(now));
  }

  tick(now) {
    this.frame = 0;
    if (this.paused) return;
    const dt = this.lastFrame ? (now - this.lastFrame) / 1000 : 1 / 60;
    this.lastFrame = now;
    const pointer = this.pendingPointer;
    this.pendingPointer = null;
    let moving = false;
    const records = [...this.balloons.values()];
    const boxes = records.map((record) => record.position.getBoundingClientRect());
    records.forEach((record, index) => {
      if (this.grab?.record !== record && record.movement.outside(boxes[index], this.viewportWidth, innerHeight)) { this.remove(record.id); return; }
      if (pointer && this.grab?.record !== record) {
        const box = boxes[index];
        record.reaction.pass(box.left + box.width / 2 - pointer.x, box.top + box.height / 2 - pointer.y, pointer.speed);
      }
      const momentum = record.movement.tick(dt);
      if (momentum) {
        record.reaction.velocity.rx -= record.movement.vy * dt * .35;
        record.reaction.velocity.ry += record.movement.vx * dt * .45;
      }
      moving = momentum || moving;
      moving = record.reaction.tick(dt, this.mobile.matches ? 2 : 12) || moving;
      record.position.style.transform = record.movement.transform();
      record.body.style.transform = record.reaction.transform();
    });
    if (moving || this.grab) this.requestFrame();
    else this.lastFrame = 0;
  }

  protectedBoxes() {
    const selectors = this.heroActive
      ? '[data-lb-logo], .lb-launch__scroll, [data-lb-dock], [data-lb-wordmark], .lb-launch__message, .lb-launch__footer'
      : '.lb-launch__message, .lb-launch__footer, [data-lb-dock], [data-lb-wordmark]';
    return [...this.root.querySelectorAll(selectors), ...(this.cookieElements || [])].map((element) => element.getBoundingClientRect())
      .filter((box) => box.width && box.height && box.bottom > 0 && box.top < innerHeight);
  }

  placeBalloon(size, side) {
    const protectedBoxes = this.protectedBoxes();
    const occupied = [...this.balloons.values()].map((record) => record.position.getBoundingClientRect());
    let best = null, bestOverlap = Infinity;
    for (let attempt = 0; attempt < 80; attempt++) {
      const inset = this.heroActive ? between(8, Math.max(8, this.viewportWidth * .4 - size)) : between(-size * .2, 4);
      const x = side ? this.viewportWidth - inset - size : inset;
      const y = between(24, Math.max(24, innerHeight - size - 32));
      if (protectedBoxes.some((box) => x < box.right + 8 && x + size > box.left - 8 && y < box.bottom + 8 && y + size > box.top - 8)) continue;
      const overlap = occupied.reduce((sum, box) => sum + Math.max(0, Math.min(x + size, box.right) - Math.max(x, box.left)) * Math.max(0, Math.min(y + size, box.bottom) - Math.max(y, box.top)), 0);
      if (overlap < bestOverlap) { best = { x, y }; bestOverlap = overlap; }
      if (!overlap) break;
    }
    return best;
  }

  startGrab(event) {
    if (this.paused || this.grab || event.button !== 0 || event.isPrimary === false) return;
    const element = event.target.closest('[data-balloon-id]');
    const record = element && this.balloons.get(Number(element.dataset.balloonId));
    if (!record) return;
    const box = record.position.getBoundingClientRect();
    record.movement.start(event.clientX, event.clientY, performance.now(), {
      minX: record.movement.x - box.left, maxX: record.movement.x + this.viewportWidth - box.right,
      minY: record.movement.y - box.top, maxY: record.movement.y + innerHeight - box.bottom
    });
    this.grab = { record, pointerId: event.pointerId, target: event.target };
    record.animation?.pause();
    record.element.setAttribute('data-grabbed', '');
    event.target.setPointerCapture(event.pointerId);
    this.pendingPointer = this.pointer = null;
    this.requestFrame();
  }

  endGrab(event, cancelled = false) {
    const grab = this.grab;
    if (!grab || (event && event.pointerId !== grab.pointerId)) return;
    this.grab = null;
    const { record, pointerId, target } = grab;
    if (event && !cancelled) record.movement.move(event.clientX, event.clientY, performance.now());
    const dragged = record.movement.end(performance.now(), cancelled);
    if (dragged && record.animation && record.keyframes) this.updateDeparture(record);
    if (dragged || cancelled) record.suppressClickUntil = performance.now() + 400;
    record.element.removeAttribute('data-grabbed');
    record.element.removeAttribute('data-dragging');
    if (target.hasPointerCapture(pointerId)) target.releasePointerCapture(pointerId);
    if (!this.paused) { record.animation?.play(); this.requestFrame(); }
    this.pointer = null;
  }

  updateDeparture(record) {
    const { animation, movement, flight } = record;
    const progress = clamp((Number(animation.currentTime) - animation.effect.getTiming().delay) / flight.duration, 0, .999999);
    const final = { ...record.keyframes.at(-1) };
    const finalY = Math.min(-(innerHeight + record.size * 3 + 100), -record.y - movement.y - Math.max(0, movement.vy / 3) - record.size * 2 - 100);
    final.transform = `translate3d(0,${finalY}px,35px) rotateX(0deg) rotateY(0deg) rotateZ(${flight.edge === 'right' ? 18 : -18}deg)`;
    // Preserve the current outer transform if grabbed during departure.
    if (progress > (flight.entrance + flight.linger) / flight.duration) {
      record.keyframes = record.keyframes.filter((frame) => frame.offset < progress);
      record.keyframes.push({ opacity: 1, transform: getComputedStyle(record.element).transform, offset: progress, easing: 'cubic-bezier(.45,0,.75,.65)' });
    } else record.keyframes.pop();
    record.keyframes.push(final);
    animation.effect.setKeyframes(record.keyframes);
  }

  queueConsent() {
    if (this.cookieFrame || this.destroyed) return;
    this.cookieFrame = requestAnimationFrame(() => { this.cookieFrame = 0; this.updateConsent(); });
  }

  updateConsent() {
    if (this.destroyed) return;
    const candidates = [...document.querySelectorAll('#shopify-pc__banner, .shopify-pc__banner__dialog, [role="dialog"], [aria-label*="Cookie"], [aria-label*="cookie"]')];
    // Pandectes renders the store's consent dialog inside an open shadow root.
    for (const host of document.querySelectorAll('pandectes-cmp')) {
      const shadow = host.shadowRoot;
      if (!shadow) continue;
      if (!this.cookieRoots.has(shadow)) {
        this.cookieObserver.observe(shadow, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'hidden', 'aria-hidden', 'open'] });
        this.cookieRoots.add(shadow);
      }
      candidates.push(...shadow.querySelectorAll('dialog, [role="dialog"]'));
    }
    const visibleElements = new Set();
    let offset = 0;
    for (const element of candidates) {
      if (this.root.contains(element) || !/cookie|privacy|privacidade/i.test(element.textContent)) continue;
      const rect = element.getBoundingClientRect();
      if (rect.height && rect.width && rect.top < innerHeight && rect.bottom > innerHeight * .5 && getComputedStyle(element).visibility !== 'hidden') {
        visibleElements.add(element);
        if (!this.cookieElements.has(element)) this.cookieResize.observe(element);
        offset = Math.max(offset, innerHeight - rect.top + 8);
      }
    }
    for (const element of this.cookieElements) if (!visibleElements.has(element)) this.cookieResize.unobserve(element);
    this.cookieElements = visibleElements;
    this.dock.style.setProperty('--lb-consent-offset', `${Math.min(offset, innerHeight - 90)}px`);
  }

  clearBalloons() {
    this.endGrab(null, true);
    clearTimeout(this.timer);
    this.timer = 0;
    for (const id of [...this.balloons.keys()]) this.remove(id);
    this.pool.clear();
    cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.lastFrame = 0;
  }

  destroy() {
    this.destroyed = true;
    this.clearBalloons();
    clearTimeout(this.resizeTimer);
    cancelAnimationFrame(this.cookieFrame);
    this.abort.abort();
    this.observer.disconnect();
    this.foldObserver?.disconnect();
    this.cookieObserver.disconnect();
    this.cookieResize.disconnect();
    this.root.removeAttribute('data-animated');
    this.root.removeAttribute('data-paused');
    instances.delete(this.root);
  }
}

function initialize(container = document) {
  container.querySelectorAll('[data-lb-launch]').forEach((root) => {
    if (!instances.has(root) && 'animate' in Element.prototype && 'IntersectionObserver' in window) instances.set(root, new LaunchScene(root));
  });
}
initialize();
document.addEventListener('shopify:section:load', (event) => initialize(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  for (const [root, scene] of instances) if (event.target.contains(root)) scene.destroy();
});
